import React from 'react';
import {
  Camera,
  ClockCounterClockwise,
  Heart,
  MapPin,
  ShieldCheck,
  CalendarBlank,
  Eye,
  Check,
  CaretLeft,
  CellSignalHigh,
  WifiHigh,
  BatteryFull,
  PawPrint,
  ArrowSquareOut,
  Play,
  CookingPot,
} from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { FeatureChipScroller, FeatureListGrid } from './FeatureTiles';

const careTiles = [
  {
    icon: CalendarBlank,
    label: 'Flexible scheduling',
    sub: 'Morning, evening or both',
  },
  {
    icon: MapPin,
    label: 'Live GPS tracking',
    sub: 'Route & pace in real time',
  },
  {
    icon: ShieldCheck,
    label: 'Certified walkers',
    sub: 'Vetted & background-checked',
  },
  {
    icon: Camera,
    label: 'Photos & video',
    sub: 'Highlights after every walk',
  },
  {
    icon: Heart,
    label: 'Soft leash handling',
    sub: 'Gentle, dog-first technique',
  },
  {
    icon: Check,
    label: 'Weather-aware routing',
    sub: 'Smart route adjustments',
  },
];

const PhoneMockup = () => (
  <div className="relative h-[560px] w-[270px] rounded-[2.8rem] border-[10px] border-neutral-950 bg-neutral-950 shadow-2xl">
    {/* Dynamic island */}
    <div className="absolute left-1/2 top-2 z-20 h-5 w-20 -translate-x-1/2 rounded-full bg-neutral-950" />
    <div className="relative flex h-full flex-col overflow-hidden rounded-[1.9rem] bg-white">
      {/* iOS status bar */}
      <div className="flex items-center justify-between px-6 pt-3 text-[11px] font-semibold text-neutral-900">
        <span>9:41</span>
        <div className="flex items-center gap-1">
          <CellSignalHigh size={12} weight="fill" />
          <WifiHigh size={12} weight="fill" />
          <BatteryFull size={14} weight="fill" />
        </div>
      </div>
      {/* App bar */}
      <div className="flex items-center gap-2 px-4 pb-2 pt-3">
        <CaretLeft size={16} weight="bold" className="text-neutral-900" />
        <span className="text-sm font-semibold text-neutral-900">Booking Details</span>
      </div>
      <div className="relative flex-1 overflow-hidden">
        <img
          src="/live-walk.png"
          alt="Live walk in progress"
          className="h-full w-full object-cover"
        />
        {/* Bottom white fade with Fresh Tails logo */}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-white via-white/80 to-transparent" />
        <img
          src="/images/freshtails-logo.png"
          alt="Fresh Tails"
          className="absolute bottom-6 left-1/2 h-14 w-auto -translate-x-1/2"
        />
      </div>
    </div>
  </div>
);

const DogWalking = () => {
  return (
    <section className="bg-white pt-10 pb-16 sm:pt-12 sm:pb-20">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <div className="landing-section-header">
          <span className="landing-eyebrow !bg-transparent !border-primary/30 !rounded-md">Dog Walking</span>
          <h2 className="landing-section-title">Daily walks, expertly managed</h2>
          <p className="landing-section-lede mx-auto">
            Track every step with live GPS, post-walk photo updates, and know your dog is in safe hands with certified walkers.
          </p>
        </div>

        <div className="relative my-16 min-h-[460px] rounded-2xl bg-neutral-900 md:min-h-[500px]">
          <div className="absolute inset-0 overflow-hidden rounded-2xl">
            <img
              src="https://images.pexels.com/photos/7179466/pexels-photo-7179466.jpeg?auto=compress&cs=tinysrgb&w=1600"
              alt="Dog walking on grass"
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/40 to-black/10" />
          </div>

          <div className="relative z-10 flex min-h-[460px] flex-col items-start justify-center gap-4 p-7 md:min-h-[500px] md:p-12">
            <div className="max-w-xs text-white sm:max-w-sm">
              <span className="inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/20 px-3 py-1 text-xs font-semibold text-white shadow-sm backdrop-blur">
                <PawPrint size={13} weight="fill" className="text-green-300" />
                Powered by Freshtails
              </span>
              <h3 className="mt-4 text-3xl font-semibold leading-tight text-white sm:text-4xl">
                Every walk is an adventure they deserve
              </h3>
              <p className="mt-3 text-base font-medium leading-relaxed text-white/85">
                Professional dog walking tailored to your pet's energy, breed, and schedule. Real-time updates so you never miss a moment.
              </p>
              <Button
                asChild
                className="mt-6 h-11 rounded-md bg-green-600 px-8 text-sm font-bold text-white hover:bg-green-700"
              >
                <a
                  href="https://freshtails.zopnote.com/login"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Book a Walk
                  <ArrowSquareOut size={16} weight="bold" />
                </a>
              </Button>
            </div>
          </div>

          {/* Phone overflowing the right edge */}
          <div className="pointer-events-none absolute -bottom-6 -top-6 right-6 z-20 hidden items-center lg:flex lg:right-10">
            <PhoneMockup />
          </div>
        </div>

        <div className="grid gap-5 lg:grid-cols-2">
          {/* Pet record */}
          <div className="rounded-2xl border border-neutral-200 bg-white p-6">
            <h3 className="text-lg font-semibold text-neutral-900">Your Pet's Complete Record</h3>
            <div className="mt-5 flex items-center gap-4">
              <img
                src="https://images.unsplash.com/photo-1633722715463-d30f4f325e24?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="Max"
                className="h-14 w-14 rounded-full object-cover"
              />
              <div>
                <p className="text-base font-semibold text-neutral-900">Max</p>
                <p className="text-xs text-neutral-500">Golden Retriever · 3 years</p>
              </div>
            </div>
            <dl className="mt-5 divide-y divide-neutral-100 text-sm">
              {[
                ['Weight', '28 kg'],
                ['Gender', 'Male'],
                ['Diet', 'Grain-free'],
                ['Last Checkup', 'Dec 1, 2025'],
              ].map(([label, value]) => (
                <div key={label} className="flex items-center justify-between py-2.5">
                  <dt className="text-neutral-500">{label}</dt>
                  <dd className="font-medium text-neutral-900">{value}</dd>
                </div>
              ))}
            </dl>
            <div className="mt-5 flex flex-wrap gap-2 border-t border-neutral-100 pt-4">
              {['Friendly', 'Energetic', 'Social', 'Playful'].map((tag) => (
                <span
                  key={tag}
                  className="rounded-full border border-primary/30 px-3 py-1 text-xs font-semibold text-primary"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {/* Transparency */}
          <div className="rounded-2xl border border-neutral-200 bg-white p-6">
            <h3 className="text-lg font-semibold text-neutral-900">Transparency in Real Time</h3>
            <div className="relative mt-5 overflow-hidden rounded-xl">
              <img
                src="/images/facility-dogs-playing.jpg"
                alt="Max checked in"
                className="h-44 w-full object-cover"
              />
              <span className="absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-full bg-green-500 px-2.5 py-1 text-[11px] font-semibold text-white shadow">
                <span className="h-1.5 w-1.5 rounded-full bg-white" />
                LIVE
              </span>
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-3">
                <p className="text-sm font-semibold text-white">Max, Checked In at 3:45 PM</p>
              </div>
            </div>
            <div className="mt-4 grid grid-cols-3 gap-3">
              {[
                { label: 'Photo Sent', meta: '2 min ago', Icon: Camera },
                { label: 'Playing', meta: '15 min ago', Icon: Play },
                { label: 'Meal Update', meta: '1 hr ago', Icon: CookingPot },
              ].map(({ label, meta, Icon }) => (
                <div key={label} className="rounded-lg bg-neutral-50 p-3 text-center">
                  <div className="mx-auto mb-2 flex h-8 w-8 items-center justify-center rounded-full bg-white">
                    <Icon size={16} weight="duotone" className="text-primary" />
                  </div>
                  <p className="text-xs font-semibold text-neutral-900">{label}</p>
                  <p className="mt-0.5 text-[10px] text-neutral-500">{meta}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <FeatureChipScroller
          tiles={careTiles}
          label="Walking features"
          className="mt-5"
        />
        <FeatureListGrid tiles={careTiles} className="mt-5" />

      </div>
    </section>
  );
};

export default DogWalking;
